import { useCallback, useEffect, useState } from 'react'
import { Card } from '@trafiflow/shared-ui'
import { Loader2, Search, Sparkles, Video, X } from 'lucide-react'
import type { GeminiScene } from './types'
import {
  type CompanyVideoAsset,
  type SceneSuggestion,
  listCompanyVideos,
  searchVideosByScene,
} from '../../repository/companyVideos.service'

interface FootageSelectorModalProps {
  scene: GeminiScene
  onSelect: (video: CompanyVideoAsset) => void
  onClose: () => void
}

type TabId = 'suggested' | 'all'

function getScoreColor(score: number): string {
  if (score >= 0.6) return 'bg-green-500'
  if (score >= 0.4) return 'bg-amber-500'
  return 'bg-gray-400'
}

function getScoreLabel(score: number): string {
  if (score >= 0.6) return 'Good match'
  if (score >= 0.4) return 'Partial match'
  return 'Low match'
}

export function FootageSelectorModal({ scene, onSelect, onClose }: FootageSelectorModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('suggested')
  const [suggestions, setSuggestions] = useState<SceneSuggestion[]>([])
  const [allVideos, setAllVideos] = useState<CompanyVideoAsset[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(true)
  const [loadingAll, setLoadingAll] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const durationSec = (scene.endTime || 0) - (scene.startTime || 0)

  const loadSuggestions = useCallback(async () => {
    setLoadingSuggestions(true)
    setError(null)
    try {
      const res = await searchVideosByScene({
        description: scene.description || '',
        visualType: scene.visualType,
        mood: scene.mood,
        camera: scene.camera,
        audio: scene.audio,
        keyElements: scene.keyElements,
        durationSec: durationSec > 0 ? durationSec : undefined,
      }, 15)
      setSuggestions(res.suggestions || [])
    } catch (err) {
      console.error('[FootageSelectorModal] Failed to load suggestions:', err)
      setError('Failed to load suggestions')
    } finally {
      setLoadingSuggestions(false)
    }
  }, [scene, durationSec])

  const loadAllVideos = useCallback(async () => {
    setLoadingAll(true)
    try {
      const res = await listCompanyVideos({ limit: 50, q: searchQuery || undefined })
      setAllVideos(res.videos || [])
    } catch (err) {
      console.error('[FootageSelectorModal] Failed to load videos:', err)
    } finally {
      setLoadingAll(false)
    }
  }, [searchQuery])

  useEffect(() => {
    loadSuggestions()
  }, [loadSuggestions])

  useEffect(() => {
    if (activeTab === 'all') {
      loadAllVideos()
    }
  }, [activeTab, loadAllVideos])

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      loadAllVideos()
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-tf-border flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-tf-text">Replace Scene Footage</h2>
              <p className="text-sm text-tf-text-muted mt-1 truncate">
                Scene {scene.sceneNumber}: {scene.description?.slice(0, 60)}
                {(scene.description?.length || 0) > 60 ? '...' : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md hover:bg-tf-bg-hover text-tf-text-muted"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-tf-border">
            <button
              type="button"
              onClick={() => setActiveTab('suggested')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'suggested'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Suggested
                {suggestions.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                    {suggestions.length}
                  </span>
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Video className="w-4 h-4" />
                All Videos
              </div>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'suggested' && (
              <>
                {loadingSuggestions && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-tf-text-muted">Finding matching footage...</span>
                  </div>
                )}

                {!loadingSuggestions && error && (
                  <div className="text-center py-12">
                    <p className="text-red-600">{error}</p>
                  </div>
                )}

                {!loadingSuggestions && !error && suggestions.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-tf-bg-hover flex items-center justify-center">
                      <Video className="w-6 h-6 text-tf-text-muted" />
                    </div>
                    <p className="text-tf-text-muted">No footage matched this scene.</p>
                    <p className="text-sm text-tf-text-muted mt-1">
                      Try the All Videos tab or upload new footage.
                    </p>
                  </div>
                )}

                {!loadingSuggestions && suggestions.length > 0 && (
                  <div className="space-y-3">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion.video.id}
                        type="button"
                        onClick={() => onSelect(suggestion.video)}
                        className="w-full text-left p-4 rounded-lg border border-tf-border hover:border-blue-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          {/* Video thumbnail placeholder */}
                          <div className="w-20 h-14 flex-shrink-0 rounded bg-tf-bg-muted flex items-center justify-center">
                            <Video className="w-6 h-6 text-tf-text-muted" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-tf-text truncate">{suggestion.video.title}</p>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                                AI
                              </span>
                            </div>

                            {/* Score bar */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex-1 h-2 rounded-full bg-tf-bg-hover overflow-hidden">
                                <div
                                  className={`h-full ${getScoreColor(suggestion.score)} transition-all`}
                                  style={{ width: `${Math.round(suggestion.score * 100)}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                suggestion.score >= 0.6 ? 'text-green-700' :
                                suggestion.score >= 0.4 ? 'text-amber-700' : 'text-gray-600'
                              }`}>
                                {Math.round(suggestion.score * 100)}% {getScoreLabel(suggestion.score)}
                              </span>
                            </div>

                            {/* Match reasons */}
                            <div className="flex flex-wrap gap-1">
                              {suggestion.matchReasons.slice(0, 3).map((reason, i) => (
                                <span
                                  key={i}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-tf-bg-hover text-tf-text-sub"
                                >
                                  {reason}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'all' && (
              <>
                {/* Search input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tf-text-muted" />
                  <input
                    type="text"
                    placeholder="Search videos by title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="w-full pl-9 pr-4 py-2.5 border border-tf-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {loadingAll && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-tf-text-muted">Loading videos...</span>
                  </div>
                )}

                {!loadingAll && allVideos.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-tf-bg-hover flex items-center justify-center">
                      <Video className="w-6 h-6 text-tf-text-muted" />
                    </div>
                    <p className="text-tf-text-muted">No videos found.</p>
                  </div>
                )}

                {!loadingAll && allVideos.length > 0 && (
                  <div className="space-y-2">
                    {allVideos.map((video) => (
                      <button
                        key={video.id}
                        type="button"
                        onClick={() => onSelect(video)}
                        className="w-full text-left p-3 rounded-lg border border-tf-border hover:border-blue-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-9 flex-shrink-0 rounded bg-tf-bg-muted flex items-center justify-center">
                            <Video className="w-4 h-4 text-tf-text-muted" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-tf-text text-sm truncate">{video.title}</p>
                            <p className="text-xs text-tf-text-muted truncate">{video.fileName}</p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {video.aiAnalysisStatus === 'ready' && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                                AI
                              </span>
                            )}
                            {video.category && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-tf-bg-hover text-tf-text-sub">
                                {video.category}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  )
}

export default FootageSelectorModal
